import * as React from 'react';
import { ViewAssetDto, getThumbnailFilePathUsingFileName, isFolder } from '@deltasierra/shared';
import { useDoubleClick } from '@deltasierra/react-hooks';
import { AssetDisplayStyle } from '../../common';
import './AssetListItem.scss';

export type AssetListItemProps = {
    asset: ViewAssetDto;
    displayStyle?: AssetDisplayStyle;
    disabled?: boolean;
    onClickSelect: (asset: ViewAssetDto) => void;
    onClickTag?: (asset: ViewAssetDto) => void;
    onClickDownload?: (asset: ViewAssetDto) => void;
    onClickExpand: (asset: ViewAssetDto) => void;
    onDoubleClick?: (asset: ViewAssetDto) => void;
};

const AssetListItem: React.FunctionComponent<AssetListItemProps> = ({
    asset,
    disabled = false,
    displayStyle = 'normal',
    onClickDownload,
    onClickExpand,
    onClickSelect,
    onClickTag,
    onDoubleClick,
}) => {
    const dateFormatOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

    const handleOnClickSelect = React.useCallback(() => {
        if (!disabled) {
            onClickSelect(asset);
        }
    }, [asset, disabled, onClickSelect]);

    const handleDoubleClick = React.useCallback(() => {
        if (!disabled && !!onDoubleClick) {
            onDoubleClick(asset);
        }
    }, [asset, disabled, onDoubleClick]);

    const ref = useDoubleClick<HTMLTableRowElement>({
        delay: 200,
        onDoubleClick: handleDoubleClick,
        onSingleClick: handleOnClickSelect,
    });

    return (
        <tr className={`asset-list-item ${displayStyle} ${disabled ? 'disabled' : ''}`} ref={ref}>
            <td className="asset-list-item-clickable" style={{ maxWidth: 50, width: 50 }}>
                {isFolder(asset) && <div className="al-folder-thumbnail-icon" />}
                {!isFolder(asset) && (
                    <img
                        height={`${asset.thumbnails.medium ? asset.thumbnails.medium.height : 256}px`}
                        src={
                            asset.thumbnails.medium
                                ? asset.thumbnails.medium.url
                                : getThumbnailFilePathUsingFileName(asset.title)
                        }
                        style={{ height: '30px', objectFit: 'contain', width: '30px' }}
                        width={`${asset.thumbnails.medium ? asset.thumbnails.medium.width : 256}px`}
                    />
                )}
            </td>
            <td className="asset-list-item-clickable" style={{ maxWidth: 240, padding: 0, width: 240 }}>
                <span className="asset-list-item-name">
                    <span title={asset.title}>{asset.title}</span>
                </span>
            </td>
            <td className="asset-list-item-date asset-list-item-clickable">
                {asset.createdAt.toLocaleDateString(undefined, dateFormatOptions)}
            </td>
            <td className="asset-list-item-date asset-list-item-clickable">
                {asset.expires ? asset.expires.toLocaleDateString(undefined, dateFormatOptions) : '-'}
            </td>
            <td className="asset-list-item-fileSize asset-list-item-clickable">
                {`${Math.round(asset.size / 1000)} kB`}
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>
                {onClickTag && (
                    <i
                        className="asset-list-item-icon glyphicon glyphicon-tags text-primary"
                        onClick={() => onClickTag(asset)}
                    />
                )}
                {onClickDownload && (
                    <i
                        className="asset-list-item-icon glyphicon glyphicon-download-alt text-primary"
                        onClick={() => onClickDownload(asset)}
                    />
                )}
                <i
                    className="asset-list-item-icon glyphicon glyphicon-search text-primary"
                    onClick={() => onClickExpand(asset)}
                />
            </td>
        </tr>
    );
};

AssetListItem.displayName = 'AssetListItem';

export default AssetListItem;
